import React from 'react';

import { Divider, Menu, MenuItem as MuiMenuItem } from '@mui/material';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AllBreakoutRoomsContainer from '../../context/AllBreakoutRoomsContext';
import { useAppMessaging } from '../../hooks/useAppMessaging';

export interface VideoTileMenuProps {
    breakoutName: string;
    anchorEl: any,
    onClose: () => void;
}

const MenuItem = styled(MuiMenuItem)`
    padding: 3px 16px;
`;

const SnapshotTileMenu: React.FC<VideoTileMenuProps> = ({ breakoutName, anchorEl, onClose }) => {
    const { sendMessage } = useAppMessaging();
    const { token, zoomId, userId, classId, meetingId, breakoutNames, classSettings } = useContainer(AllBreakoutRoomsContainer);
    const { t } = useTranslation('all_breakout_rooms');

    // const handleJoinClick = () => {
    //     sendMessage({
    //         func: 'v1_join_breakout',
    //         data: { breakout_name: breakoutName }
    //     });
    //
    //     onClose();
    // };

    const handleChatClick = () => {
        sendMessage({
            func: 'v1_open_breakout_chat',
            data: { breakout_name: breakoutName }
        });

        onClose();
    };

    const handleWebClick = () => {
        const params = {
            token,
            jwt: zoomId,
            id: meetingId,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        const url = `class_webpages.html?${qs.stringify(params)}`;

        sendMessage({
            func: 'v1_open_relative_webview',
            data: {
                url,
                width: 800,
                height: 600,
                label: 'class_webpages'
            }
        });

        onClose();
    };

    const handleVideoClick = () => {
        const params = {
            token,
            jwt: zoomId,
            id: meetingId,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        const url = `class_videos.html?${qs.stringify(params)}`;

        sendMessage({
            func: 'v1_open_relative_webview',
            data: {
                url,
                width: 800,
                height: 600,
                label: 'class_videos'
            }
        });

        onClose();
    };

    const handleFileClick = () => {
        const params = {
            token,
            jwt: zoomId,
            id: meetingId,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        const url = `files/files.html?${qs.stringify(params)}`;

        sendMessage({
            func: 'v1_open_relative_webview',
            data: {
                url,
                width: 800,
                height: 600,
                label: 'share_files'
            }
        });

        onClose();
    };

    const handleWhiteboardClick = () => {
        const params = {
            token,
            userUuid: userId,
            meeting_id: meetingId,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        const url = `react/whiteboard/sessions/${classId}?${qs.stringify(params)}`;

        sendMessage({
            func: 'v1_open_relative_webview',
            data: {
                url,
                width: 800,
                height: 578,
                label: t('whiteboard')
            }
        });

        onClose();
    };

    const handleAssignmentClick = () => {
        const params = {
            add: 'assign',
            section: 1,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        sendMessage({
            func: 'v1_open_moodle_webview',
            data: {
                width: 800,
                height: 600,
                moodle_screen: '24',
                label: 'class_assignments',
                params: `&${qs.stringify(params)}`
            }
        });

        onClose();
    };

    const handleSurveyClick = () => {
        const params = {
            add: 'assign',
            section: 1,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        sendMessage({
            func: 'v1_open_moodle_webview',
            data: {
                width: 800,
                height: 600,
                moodle_screen: '27',
                label: 'class_survey',
                params: `&${qs.stringify(params)}`
            }
        });

        onClose();
    };

    const handlePollClick = () => {
        const params = {
            add: 'assign',
            section: 1,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        sendMessage({
            func: 'v1_open_moodle_webview',
            data: {
                width: 800,
                height: 600,
                moodle_screen: '26',
                label: 'class_polls',
                params: `&${qs.stringify(params)}`
            }
        });

        onClose();
    };

    const handleAssessmentClick = () => {
        const params = {
            add: 'assign',
            section: 1,
            breakout_name: breakoutName,
            breakout_names: breakoutNames.join(',')
        };

        sendMessage({
            func: 'v1_open_moodle_webview',
            data: {
                width: 800,
                height: 600,
                moodle_screen: '25',
                label: 'class_assessments',
                params: `&${qs.stringify(params)}`
            }
        });

        onClose();
    };

    return (
        <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={onClose}
        >
            {/* Hide join button until windows knows what to do about it
            <MenuItem onClick={handleJoinClick}>
                {t('join')}
            </MenuItem>
            */}
            {classSettings?.featureSettings.chat &&
                <MenuItem onClick={handleChatClick}>
                    {t('open-chat')}
                </MenuItem>
            }
            {classSettings?.featureSettings.chat && <Divider />}
            <MenuItem onClick={handleWebClick}>
                {t('launch-webpage')}
            </MenuItem>
            <MenuItem onClick={handleVideoClick}>
                {t('launch-video')}
            </MenuItem>
            <MenuItem onClick={handleFileClick}>
                {t('launch-file')}
            </MenuItem>
            {(classSettings?.featureSettings.whiteboard && classId) &&
                <MenuItem onClick={handleWhiteboardClick}>
                    {t('launch-whiteboard')}
                </MenuItem>
            }
            <Divider />
            {classSettings?.featureSettings.assignments &&
                <MenuItem onClick={handleAssignmentClick}>
                    {t('launch-assignment')}
                </MenuItem>
            }
            {classSettings?.featureSettings.surveys &&
                <MenuItem onClick={handleSurveyClick}>
                    {t('launch-survey')}
                </MenuItem>
            }
            {classSettings?.featureSettings.polling &&
                <MenuItem onClick={handlePollClick}>
                    {t('launch-poll')}
                </MenuItem>
            }
            {classSettings?.featureSettings.classAssessments &&
                <MenuItem onClick={handleAssessmentClick}>
                    {t('launch-assessment')}
                </MenuItem>
            }
        </Menu>
    );
};

export default SnapshotTileMenu;
